import { Download, FileText, MessageCircle, Ticket, Wallet } from "lucide-react";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { useDevelopmentFixtures } from "../../api/client";
import { DateBox } from "../../components/DateBox";
import { Button, Dialog, Field, InlineNotice, SelectField } from "../../components/ui";
import { formatDate, mexicoDateKey } from "../../data/format";
import { paymentMethodLabel, type PaymentMethod } from "../../data/payment-method";
import { commerceApi, dateHoldsApi, type AutoReservation, type CaseDossier, type CasePayment, type DateConflict, type PaymentSlipInput } from "../../data/workspace";
import { listJoin } from "@rutamagica/contracts";
import { missingCaseData } from "./case-facts";
import { CaseConfirmation, hasAgreedQuote } from "./CaseConfirmation";
import { money } from "./shared";

/**
 * Money received before there is a booking (the customer agreed by message and transferred).
 * Each payment is a paid WooCommerce order; the case's next quote deducts it.
 */
export function CasePayments({ dossier, conversationId, canSend, onRecorded, onFillData, onReserved, payRequest = 0 }: {
  dossier: CaseDossier;
  conversationId: string | null;
  canSend: boolean;
  onRecorded?: () => void;
  /** Opens the case file to fill what is missing (date, group) before recording a payment. */
  onFillData?: () => void;
  /** Each increase starts "Registrar pago" (the phone's bottom bar asks for it). */
  payRequest?: number;
  /** The booking was made from here: the case shows this message on top. */
  onReserved?: (message: string) => void;
}) {
  const [payments, setPayments] = useState<CasePayment[]>([]);
  const missingData = missingCaseData(dossier);
  const [askData, setAskData] = useState(false);
  const [dataSkipped, setDataSkipped] = useState(false);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [feedback, setFeedback] = useState("");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<PaymentMethod>("transfer");
  const [paidOn, setPaidOn] = useState(() => mexicoDateKey(new Date()));
  const [note, setNote] = useState("");
  const [commandId, setCommandId] = useState(() => crypto.randomUUID());
  const [slipOpen, setSlipOpen] = useState(false);
  // The advance the current quote asks for, so the slip never asks for it again.
  const [slipAmount, setSlipAmount] = useState(() => {
    const quote = [...(dossier.quotes ?? [])].filter((q) => q.status !== "expired").sort((a, b) => b.updated_at.localeCompare(a.updated_at))[0];
    const terms = quote?.payment_terms;
    return terms && (terms.kind === "advance" || terms.kind === "reservation_fee") && terms.upfront > 0 ? String(terms.upfront) : "";
  });
  const [slipDate, setSlipDate] = useState(() => String(dossier.opportunity?.requested_date ?? dossier.leads?.[0]?.requested_date ?? "").slice(0, 10));
  const [slipPeople, setSlipPeople] = useState(() => String(dossier.opportunity?.people_count ?? dossier.leads?.[0]?.people_count ?? ""));
  // Prefilled from the sale, which keeps the group the header and the quote also use.
  const [slipTeachers, setSlipTeachers] = useState(() => (dossier.opportunity?.teachers_count == null ? "" : String(dossier.opportunity.teachers_count)));
  const [slipCompanions, setSlipCompanions] = useState(() => (dossier.opportunity?.companions_count == null ? "" : String(dossier.opportunity.companions_count)));
  // Other school groups on the slip's date; asking for a payment then needs a second click.
  const [conflicts, setConflicts] = useState<DateConflict[]>([]);
  const [conflictsAccepted, setConflictsAccepted] = useState(false);
  const ref = dossier.key;
  useEffect(() => {
    setConflictsAccepted(false);
    if (!(slipOpen || open) || !/^\d{4}-\d{2}-\d{2}$/.test(slipDate)) { setConflicts([]); return; }
    let active = true;
    dateHoldsApi.conflicts(slipDate, dossier.key).then((result) => { if (active) setConflicts(result.data); }, () => { if (active) setConflicts([]); });
    return () => { active = false; };
  }, [slipOpen, open, slipDate, dossier.key]);
  const service = dossier.service ?? null;

  const load = useCallback(() => {
    commerceApi.casePayments(ref).then(
      (result) => { setPayments(result.data); setError(""); },
      (caught) => setError(caught instanceof Error ? caught.message : "No se pudieron cargar los pagos."),
    );
  }, [ref]);
  useEffect(load, [load]);

  // A payment with the date or group still unknown: offer to fill them first, or go on.
  const startPayment = () => {
    setFeedback("");
    if (missingData.length && !dataSkipped) setAskData(true);
    else setOpen(true);
  };
  useEffect(() => {
    if (payRequest > 0) startPayment();
    // Only a new request opens it, not data changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payRequest]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const value = Math.round(Number(amount) * 100) / 100;
    if (!(value > 0)) { setFeedback("Escribe el monto recibido."); return; }
    if (conflicts.length && !conflictsAccepted) { setConflictsAccepted(true); return; }
    setBusy("record");
    setFeedback("");
    try {
      let reservation: AutoReservation | undefined;
      if (!useDevelopmentFixtures) {
        ({ auto_reservation: reservation } = await commerceApi.recordCasePayment({
          // The same id on a retry never records the payment twice.
          command_id: commandId,
          case_ref: { kind: ref.kind, id: ref.id },
          customer: { name: dossier.customer.name, ...(dossier.customer.phone ? { phone: dossier.customer.phone } : {}), ...(dossier.customer.email ? { email: dossier.customer.email } : {}) },
          amount: value,
          method,
          paid_on: paidOn,
          ...(note.trim() ? { note: note.trim() } : {}),
        }));
      }
      setOpen(false);
      setAmount("");
      setNote("");
      setCommandId(crypto.randomUUID());
      // With a quote already sent, the payment also accepted it and created the booking.
      setFeedback(
        reservation?.status === "reserved"
          ? `Pago registrado y cotización aceptada: se creó la reserva${reservation.booking_id ? ` RES-${reservation.booking_id}` : ""} con este anticipo.`
          : reservation?.status === "failed"
            ? `Pago registrado. La reserva no se creó sola: ${reservation.message} Ábrela desde Cotizar.`
            : "Pago registrado. Se creó la orden pagada en WooCommerce.",
      );
      load();
      onRecorded?.();
    } catch (caught) {
      setFeedback(caught instanceof Error ? caught.message : "No se pudo registrar el pago.");
    } finally {
      setBusy("");
    }
  };

  const run = async (key: string, task: () => Promise<unknown>, done: string) => {
    setBusy(key);
    setFeedback("");
    try {
      if (!useDevelopmentFixtures) await task();
      setFeedback(done);
    } catch (caught) {
      setFeedback(caught instanceof Error ? caught.message : "No se pudo completar.");
    } finally {
      setBusy("");
    }
  };

  const slip = (): PaymentSlipInput | null => {
    const amount = Math.round(Number(slipAmount) * 100) / 100;
    if (!(amount > 0)) { setFeedback("Escribe el monto de la ficha."); return null; }
    const people = Number(slipPeople), teachers = Number(slipTeachers), companions = Number(slipCompanions);
    return {
      customer_name: dossier.customer.name,
      // The advance holds the date; there is no separate reservation fee.
      concept: "anticipo",
      // The server completes the slip from the case (quote total and rest, full name, group).
      case_kind: ref.kind,
      case_id: ref.id,
      amount,
      ...(service ? { service } : {}),
      ...(/^\d{4}-\d{2}-\d{2}$/.test(slipDate) ? { date: slipDate } : {}),
      ...(Number.isInteger(people) && people > 0 ? { people } : {}),
      ...(service === "school" && Number.isInteger(teachers) && teachers > 0 ? { teachers } : {}),
      ...(service === "school" && Number.isInteger(companions) && companions > 0 ? { companions } : {}),
    };
  };
  const sendSlip = (how: "pdf" | "wa") => {
    const input = slip();
    if (!input) return;
    if (conflicts.length && !conflictsAccepted) { setConflictsAccepted(true); setFeedback("Hay otro grupo escolar ese día. Revisa el aviso y vuelve a tocar el botón para continuar."); return; }
    // The slip holds the date provisionally for this case; recording the payment confirms it.
    const hold = () => input.date
      ? dateHoldsApi.hold({
          case_ref: { kind: ref.kind, id: ref.id }, date: input.date, customer_name: input.customer_name,
          ...(service ? { service } : {}), ...(dossier.customer.phone ? { customer_phone: dossier.customer.phone } : {}),
          ...(input.people ? { people: input.people } : {}), ...(input.teachers ? { teachers: input.teachers } : {}), ...(input.companions ? { companions: input.companions } : {}),
        })
      : Promise.resolve(null);
    const dateNote = input.date ? " La fecha quedó apartada provisionalmente." : "";
    void run(`slip:${how}`, async () => { await hold(); return how === "pdf" ? commerceApi.downloadPaymentSlip(input) : commerceApi.sendPaymentSlip(input, conversationId!); }, (how === "pdf" ? "Ficha descargada." : "Ficha de pago enviada por WhatsApp.") + dateNote).then(() => { if (how === "wa") setSlipOpen(false); onRecorded?.(); });
  };

  const conflictNotice = conflicts.length > 0 ? (
    <InlineNotice tone="warning">
      Ya hay {conflicts.length === 1 ? "otro grupo escolar" : `${conflicts.length} grupos escolares`} el {formatDate(slipDate)}:{" "}
      {conflicts.map((c) => `${c.name}${c.people ? ` (${c.people})` : ""} · ${c.kind === "hold" ? (c.status === "confirmed" ? "apartado confirmado" : "apartado provisional") : "reserva"}`).join("; ")}.
      {conflictsAccepted ? " Toca otra vez para continuar de todos modos." : ""}
    </InlineNotice>
  ) : null;

  const pending = payments.filter((payment) => !payment.booking_id);
  // The quote already sent deducts what was received; nothing waits for "the next quote".
  const quoted = hasAgreedQuote(dossier);
  const quoteTotal = [...(dossier.quotes ?? [])].filter((q) => q.status !== "draft").sort((a, b) => b.updated_at.localeCompare(a.updated_at))[0]?.total ?? 0;
  const received = payments.reduce((sum, payment) => sum + payment.amount, 0);
  // Paid, with the quote sent, but no booking yet (paid before reservations became automatic):
  // the booking is made right here, without going to the quotes.
  const quotes = dossier.quotes ?? [];
  const toReserve = !dossier.bookings?.length && !quotes.some((quote) => quote.status === "converted" || quote.booking_id)
    ? [...quotes].filter((quote) => quote.status === "issued" || quote.status === "accepted").sort((a, b) => b.updated_at.localeCompare(a.updated_at))[0]
    : undefined;
  const reserveNow = async () => {
    setBusy("reserve");
    setFeedback("");
    try {
      const result = useDevelopmentFixtures
        ? ({ status: "reserved", quote_id: toReserve!.id, booking_id: null } as AutoReservation)
        : (await commerceApi.reserveCase(ref, crypto.randomUUID())).data;
      if (result.status === "reserved") {
        const rest = Math.max(0, (toReserve?.total ?? 0) - received);
        const message = `Reserva${result.booking_id ? ` #${result.booking_id}` : ""} creada con ${money(received, "MXN")} ya pagados.${rest > 0 ? ` Resta ${money(rest, "MXN")}.` : ""}`;
        setFeedback(message);
        onReserved?.(message);
        onRecorded?.();
      } else if (result.status === "failed") setFeedback(`No se creó la reserva: ${result.message}`);
      else setFeedback("No hay una cotización enviada para reservar.");
    } catch (caught) {
      setFeedback(caught instanceof Error ? caught.message : "No se pudo crear la reserva.");
    } finally {
      setBusy("");
    }
  };
  return (
    <section className="workspace-card case-side-payments" aria-label="Pagos y comprobantes">
      <h2>Pagos y comprobantes</h2>
      {error && <InlineNotice tone="warning">{error}</InlineNotice>}
      {payments.length > 0 ? (
        <>
          <p className="case-payments__total">Recibido: <strong>{money(received, "MXN")}</strong></p>
          <ul className="case-payments">
            {payments.map((payment) => (
              <li key={payment.order_id}>
                <div>
                  <strong>{money(payment.amount, payment.currency || "MXN")}</strong>
                  <span>{formatDate(payment.paid_on)} · {paymentMethodLabel(payment.method)} · pedido #{payment.order_id}</span>
                  <small>{payment.booking_id ? "Aplicado a la reserva" : quoted ? "Descontado de la cotización" : "Se descontará en la cotización"}</small>
                </div>
                <div className="case-actions">
                  <Button variant="secondary" icon={Download} aria-label={`Descargar comprobante del pedido ${payment.order_id}`} disabled={!!busy} onClick={() => void run(`${payment.order_id}:pdf`, () => commerceApi.downloadCaseReceipt(payment.order_id, ref, service), "Comprobante descargado.")}>
                    PDF
                  </Button>
                  {canSend && conversationId && (
                    <Button icon={MessageCircle} aria-label={`Enviar comprobante del pedido ${payment.order_id} por WhatsApp`} disabled={!!busy} onClick={() => void run(`${payment.order_id}:wa`, () => commerceApi.sendCaseReceipt(payment.order_id, ref, service, conversationId), "Comprobante enviado por WhatsApp.")}>
                      {busy === `${payment.order_id}:wa` ? "Enviando…" : "WhatsApp"}
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
          {pending.length > 0 && (
            <p className="case-payments__hint">
              {quoted && quoteTotal > 0
                ? `Resta ${money(Math.max(0, quoteTotal - received), "MXN")} de ${money(quoteTotal, "MXN")}.`
                : "La próxima cotización de este cliente descuenta lo recibido y solo cobra el resto."}
            </p>
          )}
          <CaseConfirmation dossier={dossier} paid={received} conversationId={conversationId} canSend={canSend} />
        </>
      ) : (
        <p className="case-payments__hint">Sin pagos registrados.</p>
      )}
      {toReserve && received > 0 && dossier.permissions.commerceWrite && (
        <div className="case-payments__reserve">
          <p><strong>Ya pagó {money(received, "MXN")}</strong> y tiene su cotización. Solo falta crear la reserva.</p>
          <Button icon={Ticket} disabled={!!busy} onClick={() => void reserveNow()}>
            {busy === "reserve" ? "Creando reserva…" : "Crear reserva con lo pagado"}
          </Button>
        </div>
      )}
      <div className="case-payments__actions">
        <Button icon={FileText} variant="secondary" onClick={() => { setFeedback(""); setSlipOpen(true); }}>Ficha de pago</Button>
        {dossier.permissions.commerceWrite && (
          <Button icon={Wallet} variant="secondary" onClick={startPayment}>Registrar pago</Button>
        )}
      </div>
      {feedback && <p className="quote-feedback" role="status">{feedback}</p>}
      <Dialog open={slipOpen} size="compact" title="Ficha de pago" eyebrow={dossier.customer.name} onClose={() => setSlipOpen(false)}>
        <div className="booking-action-form">
          <p>PDF con el monto y los datos para transferir. Cuando el cliente mande su comprobante, regístralo con "Registrar pago".</p>
          <div className="booking-action-form__split">
            <Field label="Anticipo" type="number" min="1" step="0.01" value={slipAmount} onChange={(event) => setSlipAmount(event.target.value)} required />
          </div>
          <DateBox label="Fecha del servicio (opcional)" name="slip_date" value={slipDate} onChange={setSlipDate} />
          <div className="booking-action-form__split">
            <Field label={service === "school" ? "Alumnos (opcional)" : "Personas (opcional)"} type="number" min="1" step="1" value={slipPeople} onChange={(event) => setSlipPeople(event.target.value)} />
            {service === "school" && <Field label="Profesores (opcional)" type="number" min="1" step="1" value={slipTeachers} onChange={(event) => setSlipTeachers(event.target.value)} />}
            {service === "school" && <Field label="Acompañantes (opcional)" type="number" min="1" step="1" value={slipCompanions} onChange={(event) => setSlipCompanions(event.target.value)} />}
          </div>
          {conflictNotice}
          <div className="case-actions">
            <Button variant="secondary" icon={Download} disabled={!!busy} onClick={() => sendSlip("pdf")}>PDF</Button>
            {canSend && conversationId && (
              <Button icon={MessageCircle} disabled={!!busy} onClick={() => sendSlip("wa")}>{busy === "slip:wa" ? "Enviando…" : "Enviar por WhatsApp"}</Button>
            )}
          </div>
          {feedback && <p className="quote-feedback" role="status">{feedback}</p>}
        </div>
      </Dialog>
      <Dialog open={askData} size="compact" title="Faltan datos del expediente" eyebrow={dossier.customer.name} onClose={() => setAskData(false)}>
        <div className="booking-action-form">
          <p>Todavía no está registrado: <strong>{listJoin(missingData)}</strong>. Llénalo ahora para que los documentos salgan completos, o registra el pago y complétalo después.</p>
          <div className="case-actions">
            {onFillData && <Button onClick={() => { setAskData(false); onFillData(); }}>Llenar ahora</Button>}
            <Button variant="secondary" onClick={() => { setAskData(false); setDataSkipped(true); setOpen(true); }}>Aún no lo sabemos</Button>
          </div>
        </div>
      </Dialog>
      <Dialog open={open} size="compact" title="Registrar pago" eyebrow={dossier.customer.name} onClose={() => setOpen(false)}>
        <form className="booking-action-form" onSubmit={submit}>
          <p>{quoted ? "Con la cotización ya enviada, este pago la acepta y crea la reserva." : "Dinero recibido antes de la cotización. Quedará como orden pagada en WooCommerce."}</p>
          {conflictNotice}
          <div className="booking-action-form__split">
            <Field label="Monto recibido" type="number" min="1" step="0.01" value={amount} onChange={(event) => setAmount(event.target.value)} required />
            <SelectField label="Cómo" value={method} onChange={(event) => setMethod(event.target.value as PaymentMethod)}>
              <option value="transfer">Transferencia</option>
              <option value="cash">Efectivo</option>
              <option value="card">Tarjeta (cobrada por fuera)</option>
            </SelectField>
          </div>
          <DateBox label="Fecha del pago" name="paid_on" value={paidOn} onChange={setPaidOn} />
          <Field label="Nota (opcional)" value={note} maxLength={300} onChange={(event) => setNote(event.target.value)} placeholder="Ejemplo: transferencia del anticipo acordado por WhatsApp" />
          <Button type="submit" icon={Wallet} disabled={busy === "record"}>{busy === "record" ? "Registrando…" : "Registrar pago"}</Button>
        </form>
      </Dialog>
    </section>
  );
}
